import swal from 'sweetalert';
import { baseApiAxios, microservicesApiAxios } from './api';
import {
  addCopyButton,
  addLinkOrderIdButton,
  addLoadAccountDetailsButton,
  addLoadVPCDetailsButton,
  addLoadOrderDetailsButton,
  addRowLoadingAnimation,
  configureTableColumns,
  scrollToPosition,
  displayErrorPopup,
  removeDisabledOptions,
} from './main';
import { addSpinner, addTableSpinner, removeSpinners } from './sidebar';
import { initDataTable } from './datatable';
import { isSearchEnabled } from './search_settings';
import { CONF } from './environment';
import OrderTabs from '../../jsx/components/forms/OrderTabs';

// ─── Table columns ───────────────────────────────────────────────────────────

export const inactiveScannerTableColumns = [
  { id: 'resource_type', name: 'Type' },
  { id: 'resource_id', name: 'Resource ID' },
  { id: 'account_id', name: 'Account ID' },
  { id: 'account_friendly_name', name: 'Account Friendly Name' },
  { id: 'account_area', name: 'Account Area' },
  { id: 'account_type', name: 'Account Type' },
  { id: 'account_stage', name: 'Account Stage' },
  { id: 'status', name: 'Status' },
  { id: 'is_inactive', name: 'Inactive' },
  { id: 'criteria_results', name: 'Criteria Results' },
  { id: 'scan_date', name: 'Scan Date' },
  { id: 'first_detected_date', name: 'First Detected' },
  { id: 'order_id', name: 'Order ID' },
];

const SCANNER_DROPDOWN_COLUMNS = [
  'resource_type',
  'status',
  'is_inactive',
  'account_stage',
  'account_area',
  'account_type',
];
const SCANNER_SEARCH_COLUMNS = [
  'resource_id',
  'account_id',
  'scan_date',
  'first_detected_date',
  'order_id',
  'account_friendly_name',
];

function createdCellDate(td, cellData) {
  if (!cellData) {
    $(td).text('-');
    return;
  }
  const d = new Date(cellData);
  if (isNaN(d.getTime())) {
    $(td).text(cellData);
    return;
  }
  $(td).text(
    d.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }) + ' UTC',
  );
}

const STATUS_CLASSES = {
  detected: 'portal-danger',
  detected_dry_run: 'portal-warning',
  order_created: 'portal-success',
  skipped_pending: 'portal-warning',
  skipped_cooldown: 'portal-warning',
  error: 'portal-danger',
  active: '',
};

function createdCellResourceId(td, cellData, rowData) {
  if (!cellData) {
    $(td).text('-');
    return;
  }
  const isVpc = rowData.resource_type === 'vpc';
  const hash = isVpc ? `?vpc_id=${cellData}#networkdetails` : `?account_id=${cellData}#accountdetails`;
  $(td).html(`<a href="${hash}" target="_blank">${cellData}</a>`);
  addCopyButton(td);
  if (isVpc) {
    addLoadVPCDetailsButton(td);
  } else {
    addLoadAccountDetailsButton(td);
  }
}

function createdCellAccountId(td, cellData) {
  if (!cellData) {
    $(td).text('-');
    return;
  }
  $(td).html(`<a href="?account_id=${cellData}#accountdetails" target="_blank">${cellData}</a>`);
  addCopyButton(td);
  addLoadAccountDetailsButton(td);
}

function createdCellStatus(td, cellData) {
  $(td).text(cellData || '-');
  const cls = STATUS_CLASSES[cellData];
  if (cls) $(td).addClass(cls);
}

function createdCellInactive(td, cellData) {
  if (cellData === true) {
    $(td).text('Yes').css('color', '#198754').css('font-weight', '600');
  } else if (cellData === false) {
    $(td).text('No').css('color', '#212529').css('font-weight', '600');
  } else {
    $(td).text('-');
  }
}

function createdCellCriteria(td, cellData, rowData) {
  if (!Array.isArray(cellData) || cellData.length === 0) {
    if (rowData && rowData.skip_reason) {
      $(td).html(
        `<span style="color: #856404; font-style: italic;" title="${rowData.skip_reason}">Skipped: ${rowData.skip_reason}</span>`,
      );
    } else {
      $(td).text('-');
    }
    return;
  }
  const passed = cellData.filter(c => c.passed).length;
  const failed = cellData.filter(c => !c.passed).length;
  const summaryColor = failed > 0 ? 'red' : 'green';
  const summaryText = failed > 0 ? `${failed} failed, ${passed} passed` : `${passed} passed`;
  const uid = 'cr-' + Math.random().toString(36).slice(2, 9);
  const details = cellData
    .map(c => {
      const color = c.passed ? 'green' : 'red';
      const icon = c.passed ? '✓' : '✗';
      const detail = c.detail ? ` — ${c.detail}` : '';
      return `<div style="color: ${color}; padding: 2px 0;">${icon} <strong>${c.name}</strong>${detail}</div>`;
    })
    .join('');
  $(td).html(
    `<a href="#" onclick="event.preventDefault(); var el=document.getElementById('${uid}'); var arrow=document.getElementById('${uid}-arrow'); el.style.display=el.style.display==='none'?'block':'none'; arrow.textContent=el.style.display==='none'?'▸':'▾';" style="color: ${summaryColor}; cursor: pointer; text-decoration: underline; text-decoration-style: dotted; text-underline-offset: 2px;"><span id="${uid}-arrow">▸</span> ${summaryText}</a>` +
      `<div id="${uid}" style="display: none; margin-top: 4px; font-size: 0.85em; max-width: 400px; white-space: normal;">${details}</div>`,
  );
}

function createdCellOrderId(td, cellData) {
  if (cellData && cellData !== '-') {
    const origin = window.location.origin;
    const orderUrl = `${origin}/?order_id=${cellData}#orderdetails`;
    $(td).html(`<a href="${orderUrl}" target="_blank">${cellData}</a>`);
  } else {
    $(td).text(cellData || '-');
  }
  addCopyButton(td);
}

export function initInactiveScannerTable(tableId) {
  configureTableColumns(tableId, inactiveScannerTableColumns);

  const columns = inactiveScannerTableColumns.map(col => ({
    visible: true,
    defaultContent: '-',
    orderable: true,
    searchable: true,
    type: 'string',
    data: col.id,
    name: `${col.id}_col`,
    title: col.name,
    createdCell:
      col.id === 'resource_id'
        ? createdCellResourceId
        : col.id === 'account_id'
          ? createdCellAccountId
          : col.id === 'status'
            ? createdCellStatus
            : col.id === 'is_inactive'
              ? createdCellInactive
              : col.id === 'criteria_results'
                ? createdCellCriteria
                : col.id === 'order_id'
                  ? createdCellOrderId
                  : col.id === 'scan_date' || col.id === 'first_detected_date'
                    ? createdCellDate
                    : addCopyButton,
  }));

  initDataTable(
    tableId,
    [
      {
        extend: 'reloadTable',
        text: 'Reload List',
        ajaxReload: false,
        methodReload: loadInactiveScannerResults,
        titleAttr: 'Reload List',
      },
      {
        extend: 'resetTable',
        text: 'Reset Search',
        ajaxReload: false,
        titleAttr: 'Clear all column filters',
      },
      {
        extend: 'csvHtml5',
        text: 'Export CSV',
        title: null,
        footer: false,
        exportOptions: {
          columns: [0, 1, 2, 3, 4, 5, 6, 7, 9, 10, 11],
        },
        titleAttr: 'Export all tracked resources as CSV file',
      },
    ],
    columns,
    null,
    null,
  );

  loadInactiveScannerResults(tableId);
}

/**
 * Rebuilds footer dropdowns and search inputs for the scanner results table.
 * Call after all data has been loaded and drawn.
 */
function rebuildScannerTableFooter(tableId) {
  const table = $('#' + tableId);
  const dataTable = table.DataTable();
  const columnsState = table.dataTable().api().state().columns;

  dataTable.columns().every(function (columnIndex) {
    const column = this;
    const dataSource = column.dataSrc();
    const title = column.header().textContent;

    if (SCANNER_DROPDOWN_COLUMNS.indexOf(dataSource) > -1) {
      const select = $('<select><option value=""></option></select>')
        .appendTo($(column.footer()).empty())
        .on('change', function () {
          const val = $.fn.dataTable.util.escapeRegex($(this).val());
          column.search(val ? '^' + val + '$' : '', true, false).draw();
        });

      column
        .data()
        .unique()
        .sort()
        .each(function (data) {
          const raw = String(data);
          const label = dataSource === 'is_inactive' ? (data === true ? 'Yes' : data === false ? 'No' : raw) : raw;
          const opt = document.createElement('option');
          opt.innerText = label;
          opt.value = raw;
          select.append(opt);

          if (
            raw &&
            columnsState[columnIndex].search &&
            columnsState[columnIndex].search.search &&
            raw.match(columnsState[columnIndex].search.search)
          ) {
            opt.selected = true;
          }
        });
    }

    if (SCANNER_SEARCH_COLUMNS.indexOf(dataSource) > -1) {
      $(column.footer()).html('<input type="text" placeholder="Search ' + title + '" />');

      if (columnsState[columnIndex].search && columnsState[columnIndex].search.search) {
        column.footer().lastChild.value = columnsState[columnIndex].search.search;
      }

      $('input', column.footer()).on('keyup change', function () {
        if (column.search() !== this.value) {
          column.search(this.value).draw();
        }
      });
    }
  });
}

export async function loadInactiveScannerResults(tableId) {
  $(() => addSpinner());
  $(() => addTableSpinner());

  try {
    let allItems = [];
    let nextToken = null;
    do {
      const params = { limit: 500 };
      if (nextToken) params.next_token = nextToken;
      const data = await baseApiAxios.get('inactive-scanner/results', null, params);
      const items = data?.results || [];
      allItems = allItems.concat(items);
      nextToken = data?.next_token || null;
    } while (nextToken);

    const table = $('#' + tableId).DataTable();
    table.clear();
    table.rows.add(allItems);
    table.columns.adjust().draw();

    rebuildScannerTableFooter(tableId);

    updateStatsBar(allItems);

    $(window).trigger('resize');
    scrollToPosition();
  } catch (e) {
    displayErrorPopup(e);
  } finally {
    removeSpinners();
  }
}

// ─── Stats Bar ───────────────────────────────────────────────────────────────

function updateStatsBar(items) {
  const total = items.length;
  const inactive = items.filter(i => i.is_inactive === true).length;
  const accounts = items.filter(i => i.resource_type === 'account').length;
  const vpcs = items.filter(i => i.resource_type === 'vpc').length;
  const errors = items.filter(i => i.status === 'error').length;

  $('#scanner-stat-total').text(total);
  $('#scanner-stat-inactive').text(inactive);
  $('#scanner-stat-accounts').text(accounts);
  $('#scanner-stat-vpcs').text(vpcs);
  $('#scanner-stat-errors').text(errors);

  // Show/hide error danger style
  const errEl = $('#scanner-stat-errors');
  if (errors > 0) {
    errEl.addClass('danger');
  } else {
    errEl.removeClass('danger');
  }

  $('#inactive-scanner-stats').show();
}

// ─── Configuration Tab ───────────────────────────────────────────────────────

let _configData = null; // cached { base_config, overrides }

export function initInactiveScannerConfig() {
  // Lazy-load config when the tab is first shown
  const tabLink = document.getElementById('inactive-scanner-config-tab-link');
  if (!tabLink) return;

  let loaded = false;
  tabLink.addEventListener('shown.bs.tab', async () => {
    if (loaded) return;
    loaded = true;
    await loadScannerConfig();
  });

  // Wire up save buttons (top and bottom)
  $('#scanner-config-save-btn, #scanner-config-save-btn-bottom').on('click', handleSaveOverrides);

  // Wire up trigger scan button
  $('#btn-trigger-inactive-scan').on('click', handleTriggerInactiveScan);

  // Rotate chevron icon on collapse toggle — Runtime Overrides
  $('#scanner-overrides-collapse').on('show.bs.collapse', () => {
    $('.scanner-collapse-icon').css('transform', 'rotate(0deg)');
  });
  $('#scanner-overrides-collapse').on('hide.bs.collapse', () => {
    $('.scanner-collapse-icon').css('transform', 'rotate(-90deg)');
  });

  // Rotate chevron icon on collapse toggle — Base Configuration
  $('#scanner-baseconfig-collapse').on('show.bs.collapse', () => {
    $('.scanner-collapse-icon-base').css('transform', 'rotate(0deg)');
  });
  $('#scanner-baseconfig-collapse').on('hide.bs.collapse', () => {
    $('.scanner-collapse-icon-base').css('transform', 'rotate(-90deg)');
  });

  // Rotate chevron icons on collapse toggle — override subsections
  $(document).on(
    'show.bs.collapse',
    '#scanner-section-ignore-lists, #scanner-section-thresholds, #scanner-section-cooldown, #scanner-section-notifications',
    function () {
      $(this).prev('.scanner-section-heading').find('.scanner-subsection-chevron').css('transform', 'rotate(0deg)');
    },
  );
  $(document).on(
    'hide.bs.collapse',
    '#scanner-section-ignore-lists, #scanner-section-thresholds, #scanner-section-cooldown, #scanner-section-notifications',
    function () {
      $(this).prev('.scanner-section-heading').find('.scanner-subsection-chevron').css('transform', 'rotate(-90deg)');
    },
  );
}

async function loadScannerConfig() {
  $('#inactive-scanner-config-loading').show();
  $('#inactive-scanner-config-content').hide();

  try {
    const data = await baseApiAxios.get('inactive-scanner/config');
    _configData = data;
    renderBaseConfig(data.base_config || {}, data.overrides || {});
    renderOverridesForm(data.overrides || {});
    $('#inactive-scanner-config-content').show();
  } catch (e) {
    displayErrorPopup(e);
  } finally {
    $('#inactive-scanner-config-loading').hide();
  }
}

// ─── Base Config Renderer (read-only) ────────────────────────────────────────

function renderBaseConfig(cfg, overrides) {
  const container = $('#inactive-scanner-base-config');
  container.empty();

  const ac = cfg.account_criteria || {};
  const vc = cfg.vpc_criteria || {};
  const dc = cfg.decline_cooldown_days || {};
  const ar = cfg.approval_reminder_days || {};
  const emails = cfg.summary_email_recipients || [];

  const oac = (overrides && overrides.account_criteria) || {};
  const ovc = (overrides && overrides.vpc_criteria) || {};
  const odc = (overrides && overrides.decline_cooldown_days) || {};
  const oar = (overrides && overrides.approval_reminder_days) || {};

  container.append(`
    <div class="row g-3">
      ${renderReadOnlySection('Account Criteria', [
        {
          label: 'Last Login Threshold',
          value: `${ac.last_login_days_threshold ?? '-'} days`,
          override: oac.last_login_days_threshold,
        },
        _isChinaPortal
          ? {
              label: 'Cost Threshold (CNY)',
              value: `¥${ac.cost_threshold_cny ?? '-'}`,
              override: oac.cost_threshold_cny,
            }
          : {
              label: 'Cost Threshold (USD)',
              value: `$${ac.cost_threshold_usd ?? '-'}`,
              override: oac.cost_threshold_usd,
            },
        { label: 'Min Account Age', value: `${ac.min_age_days ?? '-'} days`, override: oac.min_age_days },
        { label: 'Ignored Account IDs', value: renderBadgeList(ac.ignored_account_ids) },
        { label: 'Ignored Account Types', value: renderBadgeList(ac.ignored_account_types) },
        { label: 'Ignored Account Areas', value: renderBadgeList(ac.ignored_account_areas) },
        { label: 'Ignored Account Stages', value: renderBadgeList(ac.ignored_account_stages) },
      ])}
      ${renderReadOnlySection('VPC Criteria', [
        { label: 'Target VPC Types', value: renderBadgeList(vc.target_vpc_types) },
        { label: 'Min VPC Age', value: `${vc.min_age_days ?? '-'} days`, override: ovc.min_age_days },
        { label: 'Ignored VPC IDs', value: renderBadgeList(vc.ignored_vpc_ids) },
      ])}
      ${renderReadOnlySection('Cooldown &amp; Reminders', [
        {
          label: 'Decline Cooldown (Account)',
          value: `${dc['auto-close-account'] ?? '-'} days`,
          override: odc['auto-close-account'],
        },
        {
          label: 'Decline Cooldown (VPC)',
          value: `${dc['auto-destroy-vpc'] ?? '-'} days`,
          override: odc['auto-destroy-vpc'],
        },
        {
          label: 'Approval Reminder (Account)',
          value: `${ar['auto-close-account'] ?? '-'} days`,
          override: oar['auto-close-account'],
        },
        {
          label: 'Approval Reminder (VPC)',
          value: `${ar['auto-destroy-vpc'] ?? '-'} days`,
          override: oar['auto-destroy-vpc'],
        },
      ])}
      ${renderReadOnlySection('Notifications', [{ label: 'Summary Email Recipients', value: renderBadgeList(emails) }])}
    </div>
  `);
}

function renderOverrideIndicator(overrideValue) {
  if (overrideValue == null) return '';
  return ` <span class="badge bg-warning text-dark ms-2" style="font-size:0.9rem;font-weight:600;padding:0.35em 0.6em;" title="Overridden by runtime config"><i class="fas fa-pen me-1" style="font-size:0.75rem;"></i>Overridden: ${escapeHtml(String(overrideValue))}</span>`;
}

function renderReadOnlySection(title, rows) {
  const rowsHtml = rows
    .map(
      r => `
    <div class="row mb-1 py-1 align-items-baseline">
      <div class="col-5 fw-semibold" style="font-size:1rem;">${r.label}</div>
      <div class="col-7" style="font-size:0.95rem;">${r.value}${renderOverrideIndicator(r.override)}</div>
    </div>`,
    )
    .join('');
  return `
    <div class="col-md-6">
      <div class="card h-100 scanner-form-card">
        <div class="card-body">
          <h6 class="card-title border-bottom pb-2 mb-3" style="font-size:1.1rem;font-weight:700;">${title}</h6>
          ${rowsHtml}
        </div>
      </div>
    </div>`;
}

function renderBadgeList(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '<span class="text-muted fst-italic">none</span>';
  return arr
    .map(
      v =>
        `<span class="badge bg-light text-dark border me-1 mb-1" style="font-size:0.9rem;padding:0.4em 0.65em;">${escapeHtml(v)}</span>`,
    )
    .join('');
}

function escapeHtml(str) {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

// ─── Override Form Renderer ──────────────────────────────────────────────────

const OVERRIDE_LIST_FIELDS = [
  {
    key: 'ignored_account_ids',
    label: 'Ignored Account IDs',
    placeholder: 'e.g. 123456789012',
    pattern: /^\d{12}$/,
    hint: 'Must be a 12-digit AWS account ID',
    dropdown: 'account',
  },
  { key: 'ignored_account_types', label: 'Ignored Account Types', placeholder: 'e.g. operations' },
  { key: 'ignored_account_areas', label: 'Ignored Account Areas', placeholder: 'e.g. quarantine' },
  { key: 'ignored_account_stages', label: 'Ignored Account Stages', placeholder: 'e.g. closed' },
  {
    key: 'ignored_vpc_ids',
    label: 'Ignored VPC IDs',
    placeholder: 'e.g. vpc-0abc123def456',
    pattern: /^vpc-[0-9a-f]{8,17}$/,
    hint: 'Must match vpc-<hex> format',
    dropdown: 'vpc',
  },
];

const OVERRIDE_NOTIFICATION_FIELD = {
  key: 'summary_email_recipients',
  label: 'Summary Email Recipients',
  placeholder: 'e.g. team@bmw.com',
  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  hint: 'Must be a valid email address',
};

const _isChinaPortal = CONF.currency === 'CNY';

const OVERRIDE_CRITERIA_SECTIONS = [
  {
    key: 'account_criteria',
    label: 'Account Criteria Overrides',
    fields: [
      { key: 'last_login_days_threshold', label: 'Last Login Threshold (days)', type: 'number', min: 1 },
      ...(_isChinaPortal
        ? [{ key: 'cost_threshold_cny', label: 'Cost Threshold (CNY)', type: 'number', min: 0, step: '0.01' }]
        : [{ key: 'cost_threshold_usd', label: 'Cost Threshold (USD)', type: 'number', min: 0, step: '0.01' }]),
      { key: 'min_age_days', label: 'Min Account Age (days)', type: 'number', min: 1 },
    ],
  },
  {
    key: 'vpc_criteria',
    label: 'VPC Criteria Overrides',
    fields: [{ key: 'min_age_days', label: 'Min VPC Age (days)', type: 'number', min: 1 }],
  },
];

const OVERRIDE_COOLDOWN_SECTIONS = [
  {
    key: 'decline_cooldown_days',
    label: 'Decline Cooldown Overrides',
    fields: [
      { key: 'auto-close-account', label: 'Auto-Close Account (days)', type: 'number', min: 1 },
      { key: 'auto-destroy-vpc', label: 'Auto-Destroy VPC (days)', type: 'number', min: 1 },
    ],
  },
  {
    key: 'approval_reminder_days',
    label: 'Approval Reminder Overrides',
    fields: [
      { key: 'auto-close-account', label: 'Auto-Close Account (days)', type: 'number', min: 1 },
      { key: 'auto-destroy-vpc', label: 'Auto-Destroy VPC (days)', type: 'number', min: 1 },
    ],
  },
];

function renderOverridesForm(overrides) {
  const container = $('#inactive-scanner-overrides-form');
  container.empty();

  // ── Last Updated Info ──
  if (overrides.updated_by || overrides.updated_at) {
    let updatedText = '';
    if (overrides.updated_at) {
      const d = new Date(overrides.updated_at);
      updatedText =
        d.toLocaleString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        }) + ' UTC';
    }
    container.append(
      `<div class="mb-3 fw-semibold" style="font-size:1.05rem;"><i class="fas fa-clock me-2 text-primary"></i> Last updated${overrides.updated_by ? ' by <strong style="color:var(--bs-primary);text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:2px;">' + escapeHtml(overrides.updated_by) + '</strong>' : ''}${updatedText ? ' on ' + updatedText : ''}</div>`,
    );
  }

  // ── Ignore Lists ──
  let listsHtml = '<div class="mb-4">';
  listsHtml +=
    '<h5 class="scanner-section-heading" style="cursor:pointer;" data-bs-toggle="collapse" data-bs-target="#scanner-section-ignore-lists" aria-expanded="true"><i class="fas fa-chevron-down me-2 scanner-subsection-chevron" style="transition:transform 0.2s;"></i><i class="fas fa-filter me-2"></i> Ignore Lists</h5>';
  listsHtml += '<div class="collapse show" id="scanner-section-ignore-lists">';
  listsHtml +=
    '<p class="fw-semibold mb-3" style="font-size:1rem;"><i class="fas fa-info-circle text-primary me-2"></i>Items added here will be merged (set union) with the base read-only configuration lists above. They do not replace the base list.</p>';
  listsHtml += '<div class="row g-5">';
  for (const field of OVERRIDE_LIST_FIELDS) {
    const values = overrides[field.key] || [];
    listsHtml += `<div class="col-md-6"><div class="card scanner-form-card"><div class="card-body py-2 px-3">`;
    listsHtml += `<label class="form-label fw-bold" style="font-size:1.05rem;">${field.label}</label>`;
    if (field.hint) {
      listsHtml += `<div class="form-text mb-2">${field.hint}</div>`;
    }
    listsHtml += `<div class="scanner-tag-container mb-2" id="tags-${field.key}">`;
    for (const v of values) {
      listsHtml += renderTag(field.key, v);
    }
    listsHtml += `</div>`;
    if (field.dropdown) {
      listsHtml += `<div class="input-group">`;
      listsHtml += `<select class="form-control form-select selectpicker scanner-dropdown-select" id="select-${field.key}" data-field="${field.key}" data-live-search="true" data-show-subtext="true" data-size="8" data-none-selected-text="${field.placeholder}">`;
      listsHtml += `<option class="defaultDisabled" value="" disabled selected>${field.placeholder}</option>`;
      listsHtml += `</select>`;
      listsHtml += `</div>`;
    } else {
      listsHtml += `<div class="input-group input-group-sm">`;
      listsHtml += `<input type="text" class="form-control scanner-tag-input" id="input-${field.key}" placeholder="${field.placeholder}" data-field="${field.key}">`;
      listsHtml += `<button class="btn btn-outline-primary scanner-tag-add-btn" type="button" data-field="${field.key}" title="Add"><i class="fas fa-plus"></i></button>`;
      listsHtml += `</div>`;
    }
    listsHtml += `<div class="invalid-feedback" id="error-${field.key}"></div>`;
    listsHtml += `</div></div></div>`;
  }
  listsHtml += '</div>';
  listsHtml += '</div></div>';
  container.append(listsHtml);

  // ── Criteria Overrides ──
  let criteriaHtml = '<div class="mb-4">';
  criteriaHtml +=
    '<h5 class="scanner-section-heading mt-5" style="cursor:pointer;" data-bs-toggle="collapse" data-bs-target="#scanner-section-thresholds" aria-expanded="true"><i class="fas fa-chevron-down me-2 scanner-subsection-chevron" style="transition:transform 0.2s;"></i><i class="fas fa-sliders-h me-2"></i> Threshold Overrides</h5>';
  criteriaHtml += '<div class="collapse show" id="scanner-section-thresholds">';
  criteriaHtml +=
    '<p class="fw-semibold mb-3" style="font-size:1rem;"><i class="fas fa-info-circle text-primary me-2"></i>Leave blank to use the base (Terraform) value. Setting a value here replaces the base config for that section entirely.</p>';
  criteriaHtml += '<div class="row g-5">';
  for (const section of OVERRIDE_CRITERIA_SECTIONS) {
    const sectionData = overrides[section.key] || {};
    criteriaHtml += `<div class="col-md-6"><div class="card scanner-form-card"><div class="card-body py-2 px-3">`;
    criteriaHtml += `<h6 class="card-title border-bottom pb-2 mb-3" style="font-size:1.1rem;font-weight:700;">${section.label}</h6>`;
    for (const f of section.fields) {
      const val = sectionData[f.key] ?? '';
      const inputId = `override-${section.key}-${f.key}`;
      criteriaHtml += `<div class="mb-3">`;
      criteriaHtml += `<label class="form-label fw-semibold" style="font-size:0.95rem;" for="${inputId}">${f.label}</label>`;
      criteriaHtml += `<input type="${f.type}" class="form-control scanner-override-input" id="${inputId}" value="${val}" min="${f.min}" ${f.step ? `step="${f.step}"` : ''} data-section="${section.key}" data-key="${f.key}">`;
      criteriaHtml += `<div class="invalid-feedback" id="error-${inputId}"></div>`;
      criteriaHtml += `</div>`;
    }
    criteriaHtml += `</div></div></div>`;
  }
  criteriaHtml += '</div>';
  criteriaHtml += '</div></div>';
  container.append(criteriaHtml);

  // ── Cooldown / Reminder Overrides ──
  let cooldownHtml = '<div class="mb-4">';
  cooldownHtml +=
    '<h5 class="scanner-section-heading mt-5" style="cursor:pointer;" data-bs-toggle="collapse" data-bs-target="#scanner-section-cooldown" aria-expanded="true"><i class="fas fa-chevron-down me-2 scanner-subsection-chevron" style="transition:transform 0.2s;"></i><i class="fas fa-clock me-2"></i> Cooldown &amp; Reminder Overrides</h5>';
  cooldownHtml += '<div class="collapse show" id="scanner-section-cooldown">';
  cooldownHtml += '<div class="row g-5">';
  for (const section of OVERRIDE_COOLDOWN_SECTIONS) {
    const sectionData = overrides[section.key] || {};
    cooldownHtml += `<div class="col-md-6"><div class="card scanner-form-card"><div class="card-body py-2 px-3">`;
    cooldownHtml += `<h6 class="card-title border-bottom pb-2 mb-3" style="font-size:1.1rem;font-weight:700;">${section.label}</h6>`;
    for (const f of section.fields) {
      const val = sectionData[f.key] ?? '';
      const inputId = `override-${section.key}-${f.key}`;
      cooldownHtml += `<div class="mb-3">`;
      cooldownHtml += `<label class="form-label fw-semibold" style="font-size:0.95rem;" for="${inputId}">${f.label}</label>`;
      cooldownHtml += `<input type="${f.type}" class="form-control scanner-override-input" id="${inputId}" value="${val}" min="${f.min}" data-section="${section.key}" data-key="${f.key}">`;
      cooldownHtml += `<div class="invalid-feedback" id="error-${inputId}"></div>`;
      cooldownHtml += `</div>`;
    }
    cooldownHtml += `</div></div></div>`;
  }
  cooldownHtml += '</div>';
  cooldownHtml += '</div></div>';
  container.append(cooldownHtml);

  // ── Notifications ──
  const nf = OVERRIDE_NOTIFICATION_FIELD;
  const nfValues = overrides[nf.key] || [];
  let notifHtml = '<div class="mb-4">';
  notifHtml +=
    '<h5 class="scanner-section-heading mt-5" style="cursor:pointer;" data-bs-toggle="collapse" data-bs-target="#scanner-section-notifications" aria-expanded="true"><i class="fas fa-chevron-down me-2 scanner-subsection-chevron" style="transition:transform 0.2s;"></i><i class="fas fa-envelope me-2"></i> Notifications</h5>';
  notifHtml += '<div class="collapse show" id="scanner-section-notifications">';
  notifHtml += '<div class="row g-5">';
  notifHtml += `<div class="col-md-6"><div class="card scanner-form-card"><div class="card-body py-2 px-3">`;
  notifHtml += `<label class="form-label fw-bold" style="font-size:1.05rem;">${nf.label}</label>`;
  if (nf.hint) {
    notifHtml += `<div class="form-text mb-2">${nf.hint}</div>`;
  }
  notifHtml += `<div class="scanner-tag-container mb-2" id="tags-${nf.key}">`;
  for (const v of nfValues) {
    notifHtml += renderTag(nf.key, v);
  }
  notifHtml += `</div>`;
  notifHtml += `<div class="input-group input-group-sm">`;
  notifHtml += `<input type="text" class="form-control scanner-tag-input" id="input-${nf.key}" placeholder="${nf.placeholder}" data-field="${nf.key}">`;
  notifHtml += `<button class="btn btn-outline-primary scanner-tag-add-btn" type="button" data-field="${nf.key}" title="Add"><i class="fas fa-plus"></i></button>`;
  notifHtml += `</div>`;
  notifHtml += `<div class="invalid-feedback" id="error-${nf.key}"></div>`;
  notifHtml += `</div></div></div>`;
  notifHtml += '</div>';
  notifHtml += '</div></div>';
  container.append(notifHtml);

  // ── Wire tag interactions ──
  wireTagHandlers();

  // ── Load dropdown data for account/VPC selectors ──
  loadDropdownData();
}

function renderTag(fieldKey, value) {
  const safeVal = escapeHtml(value);
  return `<span class="badge bg-primary me-1 mb-1 scanner-tag" data-field="${fieldKey}" data-value="${safeVal}" style="font-size:0.95rem;cursor:default;">${safeVal} <i class="fas fa-times ms-1 scanner-tag-remove" style="cursor:pointer;" title="Remove"></i></span>`;
}

function wireTagHandlers() {
  // Remove tag
  $(document)
    .off('click.scannerTagRemove')
    .on('click.scannerTagRemove', '.scanner-tag-remove', function () {
      $(this).closest('.scanner-tag').remove();
    });

  // Add tag via button (text-input fields only)
  $(document)
    .off('click.scannerTagAdd')
    .on('click.scannerTagAdd', '.scanner-tag-add-btn', function () {
      const fieldKey = $(this).data('field');
      addTagFromInput(fieldKey);
    });

  // Add tag via Enter key (text-input fields only)
  $(document)
    .off('keydown.scannerTagInput')
    .on('keydown.scannerTagInput', '.scanner-tag-input', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const fieldKey = $(this).data('field');
        addTagFromInput(fieldKey);
      }
    });

  // Add tag via selectpicker change (dropdown fields)
  $(document)
    .off('changed.bs.select.scannerDropdown')
    .on('changed.bs.select.scannerDropdown', '.scanner-dropdown-select', function () {
      const fieldKey = $(this).data('field');
      const value = $(this).val();
      if (!value) return;

      // Check duplicates
      const existing = $(`#tags-${fieldKey} .scanner-tag`)
        .map(function () {
          return String($(this).attr('data-value'));
        })
        .get();
      if (!existing.includes(String(value))) {
        $(`#tags-${fieldKey}`).append(renderTag(fieldKey, String(value)));
      }

      // Reset select to placeholder
      $(this).val('').selectpicker('refresh');
    });
}

function addTagFromInput(fieldKey) {
  const input = $(`#input-${fieldKey}`);
  const errorEl = $(`#error-${fieldKey}`);
  const value = input.val().trim();
  if (!value) return;

  // Validate against pattern if defined
  const fieldDef =
    OVERRIDE_LIST_FIELDS.find(f => f.key === fieldKey) ||
    (OVERRIDE_NOTIFICATION_FIELD.key === fieldKey ? OVERRIDE_NOTIFICATION_FIELD : null);
  if (fieldDef && fieldDef.pattern && !fieldDef.pattern.test(value)) {
    input.addClass('is-invalid');
    errorEl.text(fieldDef.hint || 'Invalid format').show();
    return;
  }

  // Check duplicates
  const existing = $(`#tags-${fieldKey} .scanner-tag`)
    .map(function () {
      return String($(this).attr('data-value'));
    })
    .get();
  if (existing.includes(value)) {
    input.addClass('is-invalid');
    errorEl.text('Value already exists').show();
    return;
  }

  input.removeClass('is-invalid');
  errorEl.hide();
  $(`#tags-${fieldKey}`).append(renderTag(fieldKey, value));
  input.val('').focus();
}

// ─── Dropdown Data Loaders ───────────────────────────────────────────────────

async function loadDropdownData() {
  loadAccountDropdownData();
  loadVPCDropdownData();
}

async function loadAccountDropdownData() {
  const select = document.getElementById('select-ignored_account_ids');
  if (!select) return;

  $(select).attr('disabled', true).selectpicker('refresh');

  try {
    let useOpenSearch = isSearchEnabled('account_search_enable');
    let allAccounts = [];
    const params = {
      attribute_names: ['account_id', 'account_friendly_name', 'account_type', 'account_stage'].join(','),
      fields: ['account_id', 'account_friendly_name', 'account_type', 'account_stage'].join(','),
      size: 1000,
      all: true,
    };

    try {
      let result = useOpenSearch
        ? await microservicesApiAxios.getSearchAccounts(null, params)
        : await baseApiAxios.getAccounts(null, params);
      allAccounts = [...(result.items || [])];
      while (result.next_token) {
        params.next_token = result.next_token;
        result = useOpenSearch
          ? await microservicesApiAxios.getSearchAccounts(null, params)
          : await baseApiAxios.getAccounts(null, params);
        allAccounts = [...allAccounts, ...(result.items || [])];
      }
    } catch {
      // Fallback to DynamoDB if OpenSearch fails
      if (useOpenSearch) {
        delete params.next_token;
        let result = await baseApiAxios.getAccounts(null, params);
        allAccounts = [...(result.items || [])];
        while (result.next_token) {
          params.next_token = result.next_token;
          result = await baseApiAxios.getAccounts(null, params);
          allAccounts = [...allAccounts, ...(result.items || [])];
        }
      }
    }

    removeDisabledOptions(select);

    const options = allAccounts
      .sort((a, b) => (a.account_id || '').localeCompare(b.account_id || ''))
      .map(item => {
        const opt = document.createElement('option');
        opt.value = item.account_id;
        opt.dataset.content =
          `<span class="bs-dropdown-badge account">${item.account_id}</span>` +
          `<span class="bs-dropdown-item-text">${item.account_friendly_name || ''}</span>` +
          `<span class="bs-dropdown-badge">${item.account_type || ''}</span>` +
          `<span class="bs-dropdown-badge new">${item.account_stage || ''}</span>`;
        return opt;
      });

    options.forEach(opt => select.appendChild(opt));
  } catch (e) {
    console.error('Failed to load accounts for scanner config dropdown', e);
  } finally {
    $(select).attr('disabled', false).selectpicker('refresh');
  }
}

async function loadVPCDropdownData() {
  const select = document.getElementById('select-ignored_vpc_ids');
  if (!select) return;

  $(select).attr('disabled', true).selectpicker('refresh');

  try {
    let useOpenSearch = isSearchEnabled('vpc_search_enable');
    let allVPCs = [];
    const params = {
      fields: ['vpc_id', 'name', 'cidr', 'aws_account_id', 'network_type'].join(','),
      size: 2000,
      list_all: true,
    };

    try {
      let result = useOpenSearch
        ? await microservicesApiAxios.getSearchVPCs(null, params)
        : await baseApiAxios.getVPCs(null, params);
      allVPCs = [...(result.items || [])];
      while (result.next_token) {
        params.next_token = result.next_token;
        result = useOpenSearch
          ? await microservicesApiAxios.getSearchVPCs(null, params)
          : await baseApiAxios.getVPCs(null, params);
        allVPCs = [...allVPCs, ...(result.items || [])];
      }
    } catch {
      // Fallback to DynamoDB if OpenSearch fails
      if (useOpenSearch) {
        delete params.next_token;
        let result = await baseApiAxios.getVPCs(null, params);
        allVPCs = [...(result.items || [])];
        while (result.next_token) {
          params.next_token = result.next_token;
          result = await baseApiAxios.getVPCs(null, params);
          allVPCs = [...allVPCs, ...(result.items || [])];
        }
      }
    }

    removeDisabledOptions(select);

    const options = allVPCs
      .sort((a, b) => (a.vpc_id || '').localeCompare(b.vpc_id || ''))
      .map(item => {
        const opt = document.createElement('option');
        opt.value = item.vpc_id;
        opt.dataset.content =
          `<span class="bs-dropdown-badge">${item.vpc_id}</span>` +
          `<span class="bs-dropdown-badge cidr">${item.cidr || ''}</span>` +
          `<span class="bs-dropdown-item-text">${item.name || ''}</span>` +
          `<span class="bs-dropdown-badge new">${item.aws_account_id || ''}</span>`;
        return opt;
      });

    options.forEach(opt => select.appendChild(opt));
  } catch (e) {
    console.error('Failed to load VPCs for scanner config dropdown', e);
  } finally {
    $(select).attr('disabled', false).selectpicker('refresh');
  }
}

// ─── Save Overrides ──────────────────────────────────────────────────────────

function collectFormData() {
  const data = {};

  // Collect tag/list fields
  for (const field of OVERRIDE_LIST_FIELDS) {
    const values = $(`#tags-${field.key} .scanner-tag`)
      .map(function () {
        return String($(this).attr('data-value'));
      })
      .get();
    if (values.length > 0) {
      data[field.key] = values;
    }
  }

  // Collect notification field
  const nfValues = $(`#tags-${OVERRIDE_NOTIFICATION_FIELD.key} .scanner-tag`)
    .map(function () {
      return String($(this).attr('data-value'));
    })
    .get();
  if (nfValues.length > 0) {
    data[OVERRIDE_NOTIFICATION_FIELD.key] = nfValues;
  }

  // Collect criteria overrides
  for (const section of [...OVERRIDE_CRITERIA_SECTIONS, ...OVERRIDE_COOLDOWN_SECTIONS]) {
    const sectionData = {};
    let hasValues = false;
    for (const f of section.fields) {
      const inputId = `override-${section.key}-${f.key}`;
      const val = $(`#${inputId}`).val();
      if (val !== '' && val !== undefined) {
        sectionData[f.key] = f.type === 'number' ? parseFloat(val) : val;
        hasValues = true;
      }
    }
    if (hasValues) {
      data[section.key] = sectionData;
    }
  }

  return data;
}

// ─── Related Orders Tab ──────────────────────────────────────────────────────

const RELATED_ORDER_ACTIONS = ['auto-destroy-vpc', 'auto-close-account'];

const relatedOrderTableColumns = [
  { id: 'select_col' },
  { id: 'id_col', name: 'Order Id' },
  { id: 'creation_date_col', name: 'Order Date' },
  { id: 'action_col', name: 'Action' },
  { id: 'description_col', name: 'Description' },
  { id: 'account_id_col', name: 'Account Id' },
  { id: 'proposer_col', name: 'Proposer' },
  { id: 'approved_col', name: 'Approved' },
  { id: 'executed_col', name: 'Executed' },
  { id: 'execution_date_col', name: 'Execution Date' },
];

const ORDERS_DROPDOWN_COLUMNS = ['action', 'approved', 'executed'];
const ORDERS_SEARCH_COLUMNS = [
  'id',
  'creation_date',
  'payload.description',
  'account_id',
  'proposer',
  'execution_date',
];

export function initInactiveScannerOrders(tableId) {
  configureTableColumns(tableId, relatedOrderTableColumns);

  const columns = [
    {
      visible: true,
      defaultContent: '',
      orderable: false,
      searchable: false,
      data: null,
      name: 'select_col',
      class: 'details-control',
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'id',
      name: 'id_col',
      title: 'Order Id',
      createdCell: function (td) {
        addCopyButton(td);
        addLoadOrderDetailsButton(td);
        addLinkOrderIdButton(td);
      },
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'creation_date',
      name: 'creation_date_col',
      title: 'Order Date',
      createdCell: addCopyButton,
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'action',
      name: 'action_col',
      title: 'Action',
      createdCell: addCopyButton,
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'payload.description',
      name: 'description_col',
      title: 'Description',
      className: 'td-limit',
      createdCell: function (td, cellData) {
        if (cellData) addCopyButton(td);
      },
      render: function (data) {
        return data || '-';
      },
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'account_id',
      name: 'account_id_col',
      title: 'Account Id',
      createdCell: function (td) {
        addCopyButton(td);
        addLoadAccountDetailsButton(td);
      },
      render: function (data) {
        return data || '-';
      },
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'proposer',
      name: 'proposer_col',
      title: 'Proposer',
      createdCell: addCopyButton,
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'approved',
      name: 'approved_col',
      title: 'Approved',
      render: function (data) {
        return data ? data.replace(/_/g, ' ').toUpperCase() : '-';
      },
      createdCell: function (td, cellData) {
        addCopyButton(td);
        switch (cellData) {
          case 'APPROVED':
            $(td).addClass('portal-success');
            break;
          case 'DECLINED':
          case 'TIMEOUT':
            $(td).addClass('portal-danger');
            break;
          default:
            $(td).addClass('portal-neutral');
            break;
        }
      },
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'executed',
      name: 'executed_col',
      title: 'Executed',
      render: function (data) {
        return data ? data.replace(/_/g, ' ').toUpperCase() : '-';
      },
      createdCell: function (td, cellData) {
        addCopyButton(td);
        switch (cellData) {
          case 'SUCCESS':
          case 'SUCCESS_NO_ACTIONS':
            $(td).addClass('portal-success');
            break;
          case 'FAILED':
          case 'NOT_COMPLETED':
            $(td).addClass('portal-danger');
            break;
          case 'IN_PROGRESS':
            $(td).addClass('portal-warning');
            break;
          default:
            $(td).addClass('portal-neutral');
            break;
        }
      },
    },
    {
      visible: true,
      defaultContent: '',
      orderable: true,
      searchable: true,
      type: 'string',
      data: 'execution_date',
      name: 'execution_date_col',
      title: 'Execution Date',
      createdCell: addCopyButton,
      render: function (data) {
        return data || '-';
      },
    },
  ];

  initDataTable(
    tableId,
    [
      {
        extend: 'reloadTable',
        text: 'Reload Orders',
        ajaxReload: false,
        methodReload: loadRelatedOrders,
        titleAttr: 'Reload Orders',
      },
      {
        extend: 'resetTable',
        text: 'Reset Search',
        ajaxReload: false,
        titleAttr: 'Clear all column filters',
      },
    ],
    columns,
    null,
    null,
  );

  // Lazy-load orders when tab is first shown
  const tabLink = document.getElementById('inactive-scanner-orders-tab-link');
  if (!tabLink) return;

  let loaded = false;
  tabLink.addEventListener('shown.bs.tab', async () => {
    if (loaded) return;
    loaded = true;
    await loadRelatedOrders(tableId);
  });

  // Wire up detail row expand/collapse
  const detailRows = [];
  $('#' + tableId + ' tbody').on('click', 'tr td.details-control', function () {
    const tr = $(this).closest('tr');
    const dt = $('#' + tableId).DataTable();
    const row = dt.row(tr);
    const idx = $.inArray(tr.attr('id'), detailRows);

    if (row.child.isShown()) {
      tr.removeClass('details');
      row.child.hide();
      detailRows.splice(idx, 1);
    } else {
      tr.addClass('details');
      addRowLoadingAnimation(row);
      row.child(<OrderTabs data={row.data()} />).show();
      row.child()[0].setAttribute('class', 'rowDetails');
      if (idx === -1) {
        detailRows.push(tr.attr('id'));
      }
    }
  });
}

async function loadRelatedOrders(tableId) {
  $(() => addSpinner());
  $(() => addTableSpinner());

  try {
    let allOrders = [];

    for (const orderAction of RELATED_ORDER_ACTIONS) {
      let nextToken = null;

      do {
        const params = {
          size: 500,
          sort: 'creation_date:desc',
          filters: JSON.stringify({ action: { eq: orderAction } }),
        };
        if (nextToken) {
          params.next_token = nextToken;
        }

        const result = await microservicesApiAxios.getSearchOrders(null, params);
        const orders = result?.items || [];
        allOrders = allOrders.concat(orders);
        nextToken = result?.next_token || null;
      } while (nextToken);
    }

    const table = $('#' + tableId).DataTable();
    table.clear();
    table.rows.add(allOrders);
    table.columns.adjust().draw();

    rebuildOrdersTableFooter(tableId);

    $(window).trigger('resize');
  } catch (e) {
    displayErrorPopup(e);
  } finally {
    removeSpinners();
  }
}

function rebuildOrdersTableFooter(tableId) {
  const table = $('#' + tableId);
  const dataTable = table.DataTable();
  const columnsState = table.dataTable().api().state().columns;

  dataTable.columns().every(function (columnIndex) {
    const column = this;
    const dataSource = column.dataSrc();
    const title = column.header().textContent;

    if (ORDERS_DROPDOWN_COLUMNS.indexOf(dataSource) > -1) {
      const select = $('<select><option value=""></option></select>')
        .appendTo($(column.footer()).empty())
        .on('change', function () {
          const val = $.fn.dataTable.util.escapeRegex($(this).val());
          column.search(val ? '^' + val + '$' : '', true, false).draw();
        });

      column
        .data()
        .unique()
        .sort()
        .each(function (data) {
          const raw = String(data);
          const label = raw.replace(/_/g, ' ').toUpperCase();
          const opt = document.createElement('option');
          opt.innerText = label;
          opt.value = label;
          select.append(opt);

          if (
            label &&
            columnsState[columnIndex].search &&
            columnsState[columnIndex].search.search &&
            label.match(columnsState[columnIndex].search.search)
          ) {
            opt.selected = true;
          }
        });
    }

    if (ORDERS_SEARCH_COLUMNS.indexOf(dataSource) > -1) {
      $(column.footer()).html('<input type="text" placeholder="Search ' + title + '" />');

      if (columnsState[columnIndex].search && columnsState[columnIndex].search.search) {
        column.footer().lastChild.value = columnsState[columnIndex].search.search;
      }

      $('input', column.footer()).on('keyup change', function () {
        if (column.search() !== this.value) {
          column.search(this.value).draw();
        }
      });
    }
  });
}

function validateForm() {
  let valid = true;

  // Validate numeric inputs
  for (const section of [...OVERRIDE_CRITERIA_SECTIONS, ...OVERRIDE_COOLDOWN_SECTIONS]) {
    for (const f of section.fields) {
      const inputId = `override-${section.key}-${f.key}`;
      const el = $(`#${inputId}`);
      const errorEl = $(`#error-${inputId}`);
      const val = el.val();

      el.removeClass('is-invalid');
      errorEl.hide();

      if (val === '' || val === undefined) continue;

      const numVal = parseFloat(val);
      if (isNaN(numVal)) {
        el.addClass('is-invalid');
        errorEl.text('Must be a number').show();
        valid = false;
      } else if (f.min !== undefined && numVal < f.min) {
        el.addClass('is-invalid');
        errorEl.text(`Must be at least ${f.min}`).show();
        valid = false;
      }
    }
  }

  return valid;
}

async function handleSaveOverrides() {
  if (!validateForm()) {
    swal('Validation Error', 'Please fix the errors in the form before saving.', 'error');
    return;
  }

  const data = collectFormData();

  const confirmed = await swal({
    title: 'Save Overrides?',
    text: 'Are you sure you want to update the scanner configuration overrides? Changes take effect on the next scan run.',
    icon: 'warning',
    buttons: {
      cancel: { text: 'Cancel', value: null, visible: true },
      okay: { text: 'Save', value: true, visible: true },
    },
  });

  if (!confirmed) return;

  const saveBtn = $('#scanner-config-save-btn');
  const origText = saveBtn.html();
  saveBtn.prop('disabled', true).html('<i class="fas fa-spinner fa-spin me-1"></i> Saving…');

  try {
    await baseApiAxios.put('inactive-scanner/overrides', data);
    swal('Saved!', 'Overrides have been updated successfully.', 'success');

    // Refresh cached data
    _configData.overrides = data;
  } catch (e) {
    displayErrorPopup(e);
  } finally {
    saveBtn.prop('disabled', false).html(origText);
  }
}

async function handleTriggerInactiveScan() {
  const confirmed = await swal({
    title: 'Trigger Inactive Resource Scan?',
    text: 'This will start a full scan of all eligible accounts. The scan may take several minutes to complete.',
    icon: 'warning',
    buttons: {
      cancel: { text: 'Cancel', value: null, visible: true },
      okay: { text: 'Trigger Scan', value: true, visible: true },
    },
  });

  if (!confirmed) return;

  const btn = $('#btn-trigger-inactive-scan');
  const origText = btn.html();
  btn.prop('disabled', true).html('<i class="fas fa-spinner fa-spin me-2"></i>Triggering…');

  try {
    const response = await baseApiAxios.post('inactive-scanner/trigger-scan');
    swal(
      'Scan Triggered!',
      response.message || 'The inactive resource scan has been triggered successfully.',
      'success',
    );
  } catch (e) {
    const errorMessage =
      (e.response && e.response.data && e.response.data.message) || e.message || 'Failed to trigger scan.';
    swal('Cannot Trigger Scan', errorMessage, 'error');
  } finally {
    btn.prop('disabled', false).html(origText);
  }
}
